import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { Milestone, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { EvaluationMilestoneForm } from 'src/screens/shared/evaluation-milestone/EvaluationMilestoneForm'
import { queryMilestoneById } from 'src/screens/shared/implementation/details/milestoneQueries'
import { usePermissionsForKapMilestones } from 'src/service/security/PermissionHook'
import { CreateButton } from 'src/shared/button/Buttons'
import { PROJECT } from 'src/shared/constants/constants'
import { MILESTONE_RESPONSIBLE_TYPE_TYPE, MILESTONE_STATUS_TYPE } from 'src/shared/constants/milestone-constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

export const KapEvaluationMilestoneCreatePage = () => {
  const { programId, milestoneId, milestonePath } = useParams()
  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const urqlClient = useClient()

  const parsedProgramId = parseInt(programId as string)
  const parsedMilestoneId = parseInt(milestoneId as string)

  const [milestone, setMilestone] = useState<Milestone>()
  const [dataLoading, setDataLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true)
      const { data } = await urqlClient
        .query<{ milestone_by_pk: Query_Root['milestone_by_pk'] }, { id: number }>(queryMilestoneById, {
          id: parsedMilestoneId,
        })
        .toPromise()

      if (data?.milestone_by_pk) {
        setMilestone(data.milestone_by_pk)
      } else {
        notificationService.operationFailed()
      }
      setDataLoading(false)
    }

    fetchData()
  }, [notificationService, parsedMilestoneId, urqlClient])

  const { loading: permissionsLoading, canEdit } = usePermissionsForKapMilestones(
    parsedProgramId,
    milestone?.status as MILESTONE_STATUS_TYPE,
    milestone?.responsible_type as MILESTONE_RESPONSIBLE_TYPE_TYPE,
  )
  const loading = dataLoading || permissionsLoading

  const onSave = () => {
    document
      .getElementById('evaluation-milestone-form')
      ?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const onBack = (evaluationMilestoneId?: number) => {
    const route = ROUTES.KapFinalReportMilestoneRoot.nested.Details.params({ programId, milestoneId, milestonePath })
    navigate(evaluationMilestoneId ? `${route}#${evaluationMilestoneId}` : route, {
      state: evaluationMilestoneId ? { expandedEvaluationMilestoneId: evaluationMilestoneId } : undefined,
    })
  }

  if (!loading && !canEdit) {
    return <NotAuthorized inEditPage />
  }

  return (
    <ScreenLayout
      title={getMessage('label.add.evaluation.milestone')}
      actions={<CreateButton origin="header" onClick={onSave} />}
      onBack={() => onBack()}
      hasSecondLevelNavigation={false}
    >
      <PageLayout>
        <>
          {!loading && milestone?.dossier_id && (
            <>
              <HelpAndInstructions labelKey="label.help.project.evaluation.milestones.KAP" defaultExpansion />
              <EvaluationMilestoneForm dossierId={milestone?.dossier_id} projectType={PROJECT.KAP} onSubmit={onBack} />
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
